import { Navigate, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import HomePage from "./pages/HomePage";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Resources from "./pages/Resources";
import BlogPost from "./pages/BlogPost";
import JobHiring from "./pages/JobHiring";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Demo from "./pages/Demo";
import Login from "./pages/admin/Login";
import TransitionScreen from "./pages/admin/TransitionScreen";
import DashboardLayout from "./pages/admin/DashboardLayout";
import BlogList from "./pages/admin/BlogList";
import BlogCreate from "./pages/admin/BlogCreate";
import BlogEdit from "./pages/admin/BlogEdit";
import NewsletterSubscribers from "./pages/admin/NewsletterSubscribers";
import AccountRegistrations from "./pages/admin/AccountRegistrations";
import ProtectedRoute from "./components/ProtectedRoute";
import NotFound from "./pages/NotFound";
import ScrollToTop from "./components/ScrollToTop";
import ScrollToTopButton from "./components/ScrollToTopButton";
import PageLoadTransition from "./components/PageLoadTransition";
import AccountSettings from "./pages/admin/AccountSettings";
import SEO from "./components/SEO";
import { Toaster } from "@/components/ui/sonner";
import GlobalBackground from "@/components/GlobalBackground";
import IntroTextOverlay from "@/components/IntroTextOverlay";

function App() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const shouldAnimate = navigationType === "PUSH";
  const [isAtTop, setIsAtTop] = useState(true);
  const [isRouteTransitionLoading, setIsRouteTransitionLoading] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const previousPathRef = useRef(location.pathname);
  const isHomePage = location.pathname === "/";
  const isAdminPage =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/admin") ||
    location.pathname === "/login";
  const isDemoPage = location.pathname === "/demo";

  // Only show the global background on public-facing pages (not admin/dashboard)
  const showGlobalBg = !isAdminPage;

  // Hide intro overlay after 1800ms
  useEffect(() => {
    const timer = setTimeout(() => setShowIntro(false), 1800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const isPathChanged = previousPathRef.current !== location.pathname;
    previousPathRef.current = location.pathname;

    if (!isPathChanged || isAdminPage) {
      return undefined;
    }

    setIsRouteTransitionLoading(true);
    const timeoutId = setTimeout(() => {
      setIsRouteTransitionLoading(false);
    }, 900);

    return () => clearTimeout(timeoutId);
  }, [isAdminPage, location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const threshold = 200;
      setIsAtTop(window.scrollY < threshold);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const removeInjectedBacktickNodes = () => {
      const nodes = Array.from(document.body.childNodes);

      nodes.forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE) return;

        if (node.nodeValue?.trim() === "``") {
          node.parentNode?.removeChild(node);
        }
      });
    };

    removeInjectedBacktickNodes();

    const observer = new MutationObserver(() => {
      removeInjectedBacktickNodes();
    });

    observer.observe(document.body, { childList: true });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Global background — renders on all public pages, hidden on admin/dashboard */}
      {showGlobalBg && <GlobalBackground />}

      <IntroTextOverlay visible={showIntro} />
      <PageLoadTransition isLoading={isRouteTransitionLoading} />
      {isDemoPage && <SEO page="demo" />}
      <Toaster richColors position="top-right" />
      <ScrollToTop />
      <ScrollToTopButton />

      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname.split("/").slice(0, 2).join("/")}>
          <Route
            path="/"
            element={
              <PageLoadTransition isFirstLoad={!shouldAnimate}>
                <HomePage />
              </PageLoadTransition>
            }
          />
          <Route
            path="/about"
            element={
              <PageLoadTransition isFirstLoad={!shouldAnimate}>
                <About />
              </PageLoadTransition>
            }
          />
          <Route
            path="/contact"
            element={
              <PageLoadTransition isFirstLoad={!shouldAnimate}>
                <Contact />
              </PageLoadTransition>
            }
          />
          <Route
            path="/resources"
            element={
              <PageLoadTransition isFirstLoad={!shouldAnimate}>
                <Resources />
              </PageLoadTransition>
            }
          />
          <Route
            path="/resources/:slug"
            element={
              <PageLoadTransition isFirstLoad={!shouldAnimate}>
                <BlogPost />
              </PageLoadTransition>
            }
          />
          <Route
            path="/careers"
            element={
              <PageLoadTransition isFirstLoad={!shouldAnimate}>
                <JobHiring />
              </PageLoadTransition>
            }
          />
          <Route
            path="/terms"
            element={
              <PageLoadTransition isFirstLoad={!shouldAnimate}>
                <Terms />
              </PageLoadTransition>
            }
          />
          <Route
            path="/privacy"
            element={
              <PageLoadTransition isFirstLoad={!shouldAnimate}>
                <Privacy />
              </PageLoadTransition>
            }
          />
          <Route
            path="/demo"
            element={
              <PageLoadTransition isFirstLoad={!shouldAnimate}>
                <Demo />
              </PageLoadTransition>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={<Login />} />
          <Route path="/admin/transition" element={<TransitionScreen />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
            <Route index element={<Navigate to="newsletter" replace />} />
            <Route path="newsletter" element={<NewsletterSubscribers />} />
            <Route path="registrations" element={<AccountRegistrations />} />
            <Route path="blogs" element={<BlogList />} />
            <Route path="blogs/create" element={<BlogCreate />} />
            <Route path="blogs/edit/:id" element={<BlogEdit />} />
            <Route path="account-settings" element={<AccountSettings />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>

      {import.meta.env.MODE === "development" && (
        <ReactQueryDevtools
          initialIsOpen={false}
          position="bottom"
          buttonPosition="bottom-left"
          theme="dark"
        />
      )}
    </>
  );
}

export default App;
